// FILE: src/components/AdminDashboard.js

import React, { useState, useEffect } from 'react';
import { db, adminSecret } from '../firebase/config';
import { collection, onSnapshot } from "firebase/firestore";
import { Building, Bookmark, Users, Edit, ShieldAlert, PlusCircle } from 'lucide-react';
import AdminEditModal from './AdminEditModal';
import AdminAddRestaurant from './AdminAddRestaurant';

export default function AdminDashboard({ needsSeeding, seedDatabase }) {
    const [allRestaurants, setAllRestaurants] = useState([]);
    const [allBookings, setAllBookings] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRestaurant, setEditingRestaurant] = useState(null);
    const [addingRestaurant, setAddingRestaurant] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const appId = 'goeatout-app';

    useEffect(() => {
        const restaurantsUnsub = onSnapshot(collection(db, `/artifacts/${appId}/public/data/restaurants`), (snapshot) => {
            setAllRestaurants(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const bookingsUnsub = onSnapshot(collection(db, `/artifacts/${appId}/public/data/bookings`), (snapshot) => {
            const bookingData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            bookingData.sort((a, b) => b.bookedAt.toDate() - a.bookedAt.toDate());
            setAllBookings(bookingData);
        });

        const usersUnsub = onSnapshot(collection(db, `/artifacts/${appId}/public/data/users`), (snapshot) => {
            setAllUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            restaurantsUnsub();
            bookingsUnsub();
            usersUnsub();
        };
    }, [appId]);

    const handleApiResponse = (setter, message, duration = 3000) => {
        setter(message);
        setTimeout(() => setter(''), duration);
    };

    const handleSaveChanges = async (restaurantData) => {
        const payload = {
            action: 'UPDATE_RESTAURANT',
            secret: adminSecret,
            payload: restaurantData,
        };
        try {
            const response = await fetch('/api/manage-restaurant', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Failed to update restaurant.');
            handleApiResponse(setSuccess, 'Restaurant updated successfully!');
        } catch (err) {
            handleApiResponse(setError, err.message);
        }
    };

    const handleDeleteRestaurant = async (restaurantId) => {
        const payload = {
            action: 'DELETE_RESTAURANT',
            secret: adminSecret,
            payload: { id: restaurantId },
        };
        try {
            const response = await fetch('/api/manage-restaurant', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Failed to delete restaurant.');
            handleApiResponse(setSuccess, 'Restaurant deleted successfully!');
        } catch (err) {
            handleApiResponse(setError, err.message);
        }
    };
    
    const handleAddRestaurant = async (restaurantData) => {
        const payload = {
            secret: adminSecret,
            restaurantData,
        };
        try {
            const response = await fetch('/api/add-restaurant', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Failed to add restaurant.');
            handleApiResponse(setSuccess, 'Restaurant added successfully!');
        } catch (err) {
            handleApiResponse(setError, err.message);
        }
    };

    if (loading) return <div className="container mx-auto p-8 text-center">Loading Admin Data...</div>;

    if (needsSeeding) {
        return (
            <div className="container mx-auto p-8">
                <div className="text-center py-16 px-6 bg-white rounded-xl shadow-md">
                    <h2 className="mt-4 text-2xl font-semibold text-gray-800">Admin Setup Required</h2>
                    <p className="text-gray-500 mt-2 max-w-2xl mx-auto">The database is currently empty. As an admin, you can add the initial sample restaurants.</p>
                    <button 
                        onClick={() => seedDatabase()}
                        className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Add Sample Restaurants
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {editingRestaurant && (
                <AdminEditModal 
                    restaurant={editingRestaurant} 
                    users={allUsers}
                    onClose={() => setEditingRestaurant(null)}
                    onSave={handleSaveChanges}
                    onDelete={handleDeleteRestaurant}
                />
            )}
            {addingRestaurant && (
                <AdminAddRestaurant
                    users={allUsers}
                    onClose={() => setAddingRestaurant(false)}
                    onAdd={handleAddRestaurant}
                />
            )}
            <div className="container mx-auto p-4 sm:p-8 animate-fade-in-up">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Admin Dashboard</h1>
                        <p className="text-gray-500">Platform-wide overview of all restaurants and bookings.</p>
                    </div>
                    <button onClick={() => setAddingRestaurant(true)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 flex items-center gap-2">
                        <PlusCircle size={18}/> Add Restaurant
                    </button>
                </div>

                {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert"><p>{error}</p></div>}
                {success && <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4" role="alert"><p>{success}</p></div>}
                {!adminSecret && (
                     <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 flex items-center gap-3" role="alert">
                        <ShieldAlert/>
                        <div>
                            <p className="font-bold">Security Warning</p>
                            <p>Admin secret is not set. Management functions will not work. Set `REACT_APP_ADMIN_SECRET` in your Cloudflare environment variables.</p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-md flex items-center"><div className="bg-blue-100 p-4 rounded-full mr-4"><Building className="h-8 w-8 text-blue-600" /></div><div><p className="text-gray-500 text-sm">Total Restaurants</p><p className="text-3xl font-bold text-gray-800">{allRestaurants.length}</p></div></div>
                    <div className="bg-white p-6 rounded-xl shadow-md flex items-center"><div className="bg-green-100 p-4 rounded-full mr-4"><Bookmark className="h-8 w-8 text-green-600" /></div><div><p className="text-gray-500 text-sm">Total Bookings</p><p className="text-3xl font-bold text-gray-800">{allBookings.length}</p></div></div>
                    <div className="bg-white p-6 rounded-xl shadow-md flex items-center"><div className="bg-purple-100 p-4 rounded-full mr-4"><Users className="h-8 w-8 text-purple-600" /></div><div><p className="text-gray-500 text-sm">Registered Users</p><p className="text-3xl font-bold text-gray-800">{allUsers.length}</p></div></div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">All Restaurants</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {allRestaurants.map(r => (
                                    <tr key={r.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{allUsers.find(u => u.uid === r.ownerId)?.email || 'Not Assigned'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button onClick={() => setEditingRestaurant(r)} className="text-indigo-600 hover:text-indigo-900"><Edit size={20}/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">All Bookings</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guests</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voucher</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booked At</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {allBookings.map(b => (
                                    <tr key={b.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{b.restaurantName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(b.date).toLocaleDateString()} at {b.time}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{b.guests}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{b.voucher.description}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{b.bookedAt.toDate().toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}
